// Pure expiring-documents rows -> CSV text for the admin export (task 23).
// React-free and unit-tested, matching vendorCsv.ts. Column order is explicit
// so the export stays stable.
import type { Company } from '@l2u/ui/lib/types.ts';
import { daysUntilExpirySydney } from '@l2u/shared/sydneyDates';
import { csvCell } from './vendorCsv.ts';

/** Structural subset of the page's ExpiringRow that this builder reads. */
export type ExpiringCsvInputRow = {
  file_name: string;
  expires_at: string;
  submission: {
    company_id: string;
    company: { company_name: string } | null;
    question: { label: string } | null;
  } | null;
};

export const EXPIRING_CSV_HEADER =
  'vendor,document,expires,days_remaining,stations,contractor_type';

/**
 * CSV of the rows the admin is currently looking at.
 *
 * The caller passes the FILTERED rows deliberately — exporting everything would
 * not match the on-screen list the admin just narrowed down.
 *
 * `days_remaining` is negative for documents that have already lapsed, which is
 * what makes the export sortable into "worst first" in a spreadsheet.
 * Stations and contractor type come from `companyById` (the admin company list);
 * a row whose company isn't loaded still exports with its vendor and document,
 * just without those two columns. Both are lists, joined by `csvCell`.
 */
export function buildExpiringCsv(
  rows: ExpiringCsvInputRow[],
  // Only the three columns read below — see expiringFilters.ts for why this is
  // not Map<string, Company>.
  companyById: ReadonlyMap<string, Pick<Company, 'company_name' | 'stations' | 'contractor_types'>>,
  now: Date,
): string {
  const lines = rows.map((row) => {
    const company = row.submission ? companyById.get(row.submission.company_id) : undefined;
    const days = daysUntilExpirySydney(row.expires_at, now) ?? 0;
    return [
      csvCell(row.submission?.company?.company_name ?? company?.company_name ?? ''),
      // The question label names the compliance requirement; the file name is
      // the fallback when the question could not be resolved.
      csvCell(row.submission?.question?.label ?? row.file_name),
      csvCell(row.expires_at.slice(0, 10)),
      csvCell(days),
      csvCell(company?.stations ?? ''),
      csvCell(company?.contractor_types ?? ''),
    ].join(',');
  });
  return [EXPIRING_CSV_HEADER, ...lines].join('\r\n');
}
